// 1:1 port from age-of-mcp v3 (jeweller.ts). The flattened part literals are
// reconstructed from the original helper functions; geometry is parity-gated
// identical.
//
// v3 jeweller — a refined little limewashed workshop under a GOLD
// (ROOF_PRECIOUS) pyramid roof, whose icon is an OVERSIZED teal GEM crystal
// raised on a stone pedestal at the yard corner. The gem-cutting bench stands
// in the OPEN forecourt with three small cut gems on it.

import * as THREE from "three";
import { defineModel, benchWork, styleV3, type Part } from "../../lib";

const { box, poles, pyramid, slab, GEM, PLASTER, ROOF_PRECIOUS, STONE, TIMBER, WOOD } = styleV3;

const BASE = 0.12;            // foundation slab top
const HX = -0.12, HZ = -0.14; // the hut, set back from the forecourt
const WALL_H = 0.55;

// The hut walls, `h` tall on the foundation.
function hut(h: number): Part {
  return box(0.58, h, 0.56, PLASTER, [HX, BASE + h / 2, HZ]);
}

// The gold pyramid roof — ONE part, the precious-goods code (stage 3 only).
function roof(): Part {
  return pyramid(0.72, 0.34, ROOF_PRECIOUS, HX, BASE + WALL_H, HZ);
}

// The plank door on the hut front.
function door(): Part {
  return box(0.2, 0.36, 0.05, TIMBER, [HX, BASE + 0.18, HZ + 0.3]);
}

// The open gem-cutting bench on the forecourt.
function bench(): Part[] {
  return [
    box(0.5, 0.06, 0.2, WOOD, [0.1, BASE + 0.3, 0.32]),
    box(0.06, 0.27, 0.06, WOOD, [-0.11, BASE + 0.135, 0.32]),
    box(0.06, 0.27, 0.06, WOOD, [0.31, BASE + 0.135, 0.32]),
  ];
}

// A small faceted gem on the bench top.
function gemOnBench(x: number): Part {
  return {
    geometry: new THREE.OctahedronGeometry(0.05), color: GEM,
    position: [x, BASE + 0.38, 0.32],
  };
}

// The OVERSIZED gem crystal on its stone pedestal — the icon from above.
function gemStand(): Part[] {
  return [
    box(0.16, 0.3, 0.16, STONE, [0.36, BASE + 0.15, -0.22]),
    {
      geometry: new THREE.OctahedronGeometry(0.16), color: GEM,
      position: [0.36, BASE + 0.45, -0.22], rotation: [0, 0.4, 0],
      scale: [1, 1.35, 1],
    },
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.88, 0.88)];
  if (s === 1) return [slab(0.88, 0.88), hut(0.26), ...poles(0.82, 0.82, 0.6)];
  if (s === 2) return [slab(0.88, 0.88), hut(WALL_H), door(), ...bench(), ...poles(0.82, 0.82, 1.0)];
  return [
    slab(0.88, 0.88), hut(WALL_H), roof(), door(),
    ...bench(), gemOnBench(-0.04), gemOnBench(0.1), gemOnBench(0.24),
    ...gemStand(),
  ];
}

export const jewellerV3 = defineModel({
  id: "jeweller-v3",
  tiles: { w: 2, h: 2 },
  batchId: "45-optimized",
  name: "Jeweller (v3)",
  tags: ["building","jeweller","low-poly","age-of-mcp","v3"],
  summary: "Low-poly jeweller.",
  description: "A gem-cutter's bench that cuts raw crystals into faceted jewels.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "bench", position: [0.1, 0.12, 0.54], facing: Math.PI },
  ],
  interactions: [
    { id: "work", label: "At the bench", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
